import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/** Compact recommendation rows share the existing product-card Quick Add flow. */
class DeluluCartRecommendations extends Component {
  #abortController;
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
  }

  #setup() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#abortController = new AbortController();
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;
    track.addEventListener('scroll', this.#syncControls, { passive: true, signal: this.#abortController.signal });
    this.#resizeObserver = new ResizeObserver(this.#syncControls);
    this.#resizeObserver.observe(track);
    this.#syncControls();
  }

  #syncControls = () => {
    const { track, previous, next } = this.refs;
    if (!(track instanceof HTMLElement)) return;
    const offset = Math.abs(track.scrollLeft);
    if (previous instanceof HTMLButtonElement) previous.disabled = offset < 2;
    if (next instanceof HTMLButtonElement) next.disabled = offset >= track.scrollWidth - track.clientWidth - 2;
  };

  #scroll(direction) {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: direction * rtl * (track.clientWidth + 10), behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  previous() { this.#scroll(-1); }
  next() { this.#scroll(1); }
}

if (!customElements.get('delulu-cart-recommendations')) {
  customElements.define('delulu-cart-recommendations', DeluluCartRecommendations);
}
